import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/config';
import { ROUTES } from '../../../constants';
import { COLLECTION_NAME, GameListEntry } from '../../GameList/types';
import { formatHltb, hltbUrl } from '../../GameList/hltb';
import { pinkLink } from './linkColor';

const MAX_GAMES = 3;

const NowPlayingCard = () => {
  const [games, setGames] = useState<GameListEntry[] | null>(null);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const snapshot = await getDocs(collection(db, COLLECTION_NAME));
        // Top of the In Progress list on Game List across every platform,
        // skipping paused games.
        const inProgress = snapshot.docs
          .map((entry) => ({ id: entry.id, ...entry.data() }) as GameListEntry)
          .filter(
            (game) => game.state === 'inProgress' && !game.paused
          )
          .sort((a, b) => a.order - b.order);
        setGames(inProgress);
      } catch (err) {
        console.error(err);
        setGames([]);
      }
    };

    fetchGames();
  }, []);

  // Pausing drops the game from this card and the next one moves up.
  const pauseGame = async (game: GameListEntry) => {
    setGames((prev) => (prev ? prev.filter((g) => g.id !== game.id) : prev));
    try {
      await updateDoc(doc(db, COLLECTION_NAME, game.id), { paused: true });
    } catch (err) {
      console.error(err);
      setGames((prev) =>
        prev ? [...prev, game].sort((a, b) => a.order - b.order) : prev
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h6" color="primary" gutterBottom>
        Now Playing
      </Typography>

      {!games ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : games.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          No games in progress right now.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
          {games.slice(0, MAX_GAMES).map((game) => (
            <Box key={game.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src={game.image}
                alt=""
                sx={{
                  width: 60,
                  height: 34,
                  borderRadius: 0.5,
                  objectFit: 'cover',
                  flexShrink: 0,
                  bgcolor: 'action.hover',
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {game.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {game.platform} ·{' '}
                  <Link
                    href={hltbUrl(game)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: pinkLink,
                      textDecorationStyle: 'dotted',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {formatHltb(game)}
                  </Link>
                </Typography>
              </Box>
              <Tooltip title="Mark as paused">
                <IconButton
                  size="small"
                  onClick={() => pauseGame(game)}
                  sx={{ p: 0.25, color: 'text.disabled' }}
                >
                  <PauseCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <Typography variant="caption" align="right" sx={{ mt: 1.25 }}>
        <Link
          component={RouterLink}
          to={ROUTES.gameList}
          sx={{ color: pinkLink, fontWeight: 700 }}
        >
          Open Game List
          {games && games.length > MAX_GAMES ? ` (${games.length})` : ''}
        </Link>
      </Typography>
    </Box>
  );
};

export default NowPlayingCard;
